import { ApiResponseData, CursorPaginationData, skipioAxios } from ".";

export type MessageType =
  | "SmsMessage"
  | "ComplianceSmsMessage"
  | "InboundSmsMessage"
  | "VoicemailMessage"
  | "MilestoneMessage"
  | "SystemMessage";

type MessageMetadata = {
  birthday?: {
    day: number;
    month: number;
  };
  autoResponder?: {
    toId: string;
  };
};

type Milestone = {
  name: string;
  deletedAt: string | null;
};

export type Message = {
  id: string;
  authorAvatarUrl?: string | null;
  authorId: string | null;
  authorName: string | null;
  body: string;
  campaign?: { id: string; name: string } | null;
  campaignId: string | null;
  contact?: {
    id: string;
    firstName: string;
    lastName: string | null;
    phoneMobile: string;
  };
  contactId: string;
  deliverAt: string | null;
  deliveredAt: string | null;
  direction: "outbound" | "inbound";
  failedAt: string | null;
  failedReason: string | null;
  human: boolean;
  media: { url: string; type: string }[];
  messageType: MessageType;
  meta: MessageMetadata;
  milestone?: Milestone;
  milestoneId: string | null;
  providerPhoneNumber: { phoneNumber: string; label: string } | null;
  /** @deprecated Consider messageTime instead */
  time: string;
  messageTime: string;
  trueUserId: string;
  trueUser?: {
    id: string;
    firstName: string;
    lastName: string;
    avatarUrl: string;
  };
  userId: string | null;
  user?: { id: string; firstName: string; lastName: string; avatarUrl: string };
  voicemailId: null | string;
};

type CursorPageParams = {
  cursor: string;
  direction: "forward" | "backward";
};

export const fetchMessages = async (
  contactId: string,
  pageParams?: CursorPageParams
) => {
  const { cursor, direction } = pageParams || {
    cursor: "",
    direction: "forward",
  };

  const res = await skipioAxios({
    method: "GET",
    url: "messages",
    params: {
      ...(direction === "forward" ? { afterId: cursor } : { beforeId: cursor }),
      contactId,
      include: ["milestone", "user", "true_user", "campaign"],
    },
  });

  return res.data as ApiResponseData<Message[], CursorPaginationData>;
};

export const fetchContactScheduledMessages = async (
  contactId: string,
  pageParams?: CursorPageParams
) => {
  const res = await skipioAxios({
    method: "GET",
    url: "messages/scheduled",
    params: {
      afterId: pageParams?.cursor || "",
      contactId,
    },
  });

  return res.data as ApiResponseData<
    Message[],
    CursorPaginationData & { totalCount: boolean }
  >;
};

export const fetchGroupScheduledMessages = async (
  groupId: string,
  pageParams?: CursorPageParams
) => {
  const res = await skipioAxios({
    method: "GET",
    url: "messages/scheduled",
    params: {
      afterId: pageParams?.cursor || "",
      contactGroupId: groupId,
    },
  });

  return res.data as ApiResponseData<Message[], CursorPaginationData>;
};

type MilestoneMessage = Message &
  Required<Pick<Message, "milestone">> &
  Required<Pick<Message, "contact">>;

export const fetchUserMilestoneMessages = async (
  userId: string,
  pageParams?: CursorPageParams
) => {
  const res = await skipioAxios({
    method: "GET",
    url: "messages",
    params: {
      afterId: pageParams?.cursor || "",
      messageType: "MilestoneMessage",
      __as: userId,
      include: ["milestone", "contact"],
    },
  });

  return res.data as ApiResponseData<MilestoneMessage[], CursorPaginationData>;
};

export const fetchMessage = async (
  id: string,
  options: { skipRenderBody: boolean }
) => {
  const res = await skipioAxios({
    method: "GET",
    url: `messages/${id}`,
    params: {
      ...options,
    },
  });

  return res.data as ApiResponseData<Message>;
};
